import { parseUrl } from 'query-string';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { countryCodes } from '@/constants/countryCodes';
import { setMapboxPlace } from '@/redux/modules/mapboxPlace';
import apiRequest from '@/services/apiRequest';
import {
  IMapboxPlacesResponse,
  IMapboxSuggestion,
  IMapboxSuggestionsResponse,
} from '@/services/types/mapbox/mapboxApi';
import { getCookie, setCookie } from '@/utility/cookie';
import { getParamAsString } from '@/utility/queryParams';

import useDebounce from './useDebounce';
import { useGeoDataMyIp } from './useGeoDataMyIp';
import usePrevious from './usePrevious';

const MAPBOX_BASE_URL = 'https://api.mapbox.com';
const MAPBOX_TOKEN = process.env.mapboxApiToken || 'xxx';
const OUTDOORSY_MAPBOX_SESSION_TOKEN_COOKIE = 'outdoorsy-mapbox-session-token';

type TMapboxQuery = {
  text: string;
  location?: Array<number>;
  types?: string;
  limit?: number;
  // whether to perform a reverse geocoding lookup (from lng/lat to place name)
  isReverseLookup?: boolean;
};

// Module-scoped cache for suggest results — survives re-renders, cleared on page reload
const MAX_SUGGEST_CACHE_SIZE = 50;
const suggestCache = new Map<string, IMapboxSuggestionsResponse['suggestions']>();

const genMapboxSessionToken = () => {
  const sessionToken = uuid();
  const expireInSixtyMinutes = 1 / 24;
  setCookie(OUTDOORSY_MAPBOX_SESSION_TOKEN_COOKIE, sessionToken, {
    expires: expireInSixtyMinutes,
  });
  return sessionToken;
};

const getMapboxSessionToken = (forceResetCookie?: boolean) => {
  // attempt to get the session token from the cookie
  const sessionCookieToken = getCookie(OUTDOORSY_MAPBOX_SESSION_TOKEN_COOKIE);

  if (!sessionCookieToken) {
    // if the cookie doesn't exist or has expired
    // generate a new token and set a new cookie to expire in 60 minuttes (per mapbox documentation)
    const sessionToken = genMapboxSessionToken();
    return sessionToken;
  }

  if (forceResetCookie) {
    // the mapbox documentations states that on retrieve we need to terminate the current session
    // generate a new token, but use the previous token from the cookie for the retrieve request
    genMapboxSessionToken();
  }

  return sessionCookieToken;
};

export const genRetrieveMapboxUrl = (mapboxId: string): string => {
  if (!mapboxId) {
    return '';
  }

  const sessionToken = getMapboxSessionToken(true);
  const url = new URL(`${MAPBOX_BASE_URL}/search/searchbox/v1/retrieve/${mapboxId}`);
  url.search = new URLSearchParams({
    session_token: sessionToken,
    access_token: MAPBOX_TOKEN,
  }).toString();
  return url.toString();
};

// Search Box API is the default address-search path. The v5 Geocoding branch is
// retained only for callers that still consume the legacy .features response shape
// (see quoteDelivery.ts delivery-center lookup).
export const genMapboxUrl = (query: TMapboxQuery, useSearchBoxApi?: boolean): string => {
  const { location, isReverseLookup } = query;

  // we either perform reverse geocoding lookup by lng/lat or a geocoding lookup by place name
  const searchText =
    isReverseLookup && location ? encodeURI(location.join(',')) : encodeURI(query.text);
  let url = new URL(`${MAPBOX_BASE_URL}/geocoding/v5/mapbox.places/${searchText}.json`);

  let params = {};
  if (useSearchBoxApi) {
    const sessionToken = getMapboxSessionToken();
    url = new URL(`${MAPBOX_BASE_URL}/search/searchbox/v1/suggest`);
    params = {
      q: searchText,
      session_token: sessionToken,
      access_token: MAPBOX_TOKEN,
      country: countryCodes.join(','),
      ...(location ? { proximity: location.join(',') } : {}),
      types: 'place,locality,neighborhood,address,poi',
      poi_category:
        'outdoors,airport,park,tourist_attraction,river,monument,lake,mountain,forest,hostel,island,canal,nature_reserve,rest_area,campground,fair_grounds,beach,casino,marina,viewpoint,pier,picnic_shelter,sports_center,vineyard,racetrack,climbing,waterfall,fishing,resort,hunting_area,water_park,cave,town,trailhead,surf_spot,mountain_hut,boat_launch,rafting_spot,city,bed_and_breakfast,theme_park',
      limit: String(query.limit || 6),
    };
  } else {
    params = {
      access_token: MAPBOX_TOKEN,
      country: countryCodes.join(','),
      ...(location ? { proximity: location.join(',') } : {}),
      types: query.types || 'region,place,district,locality,poi',
      limit: String(query.limit || 6),
    };
  }

  url.search = new URLSearchParams(params).toString();
  return url.toString();
};

export const selectBestMapboxSuggestion = (
  url: string,
  suggestions: IMapboxSuggestion[],
): IMapboxSuggestion | null => {
  if (!suggestions || suggestions.length === 0) return null;

  // Extract query text from URL (q parameter for new API)
  const queryText = decodeURI(getParamAsString(parseUrl(url).query.q || '') || '');

  // If no query text found, return first result
  if (!queryText) return suggestions[0] || null;

  const queryLower = queryText.toLowerCase().trim();

  const matchingAny = suggestions.find(
    s =>
      s.name.toLowerCase().includes(queryLower) ||
      s.place_formatted?.toLowerCase().includes(queryLower),
  );

  return matchingAny || suggestions[0] || null;
};

function useMapboxQuery(query?: TMapboxQuery | null, delay?: number) {
  const { data: geoDataMyIp } = useGeoDataMyIp(!!query?.location);
  const location = geoDataMyIp ? [geoDataMyIp.lng, geoDataMyIp.lat] : undefined;
  const url = query ? genMapboxUrl({ ...query, location: query?.location ?? location }, true) : '';
  const debouncedUrl = useDebounce<typeof url>(url, delay);
  const previousUrl = usePrevious(debouncedUrl);
  const [results, setResults] = useState<
    IMapboxPlacesResponse['features'] | IMapboxSuggestionsResponse['suggestions']
  >([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (debouncedUrl === previousUrl) return;
    if (!debouncedUrl) {
      setResults([]);
      dispatch(setMapboxPlace(null));
      return;
    }
    // Check suggest cache before making API call
    const cached = suggestCache.get(debouncedUrl);
    if (cached) {
      setResults(cached);
      dispatch(setMapboxPlace(selectBestMapboxSuggestion(debouncedUrl, cached)));
      return;
    }

    let isCanceled = false;
    const fetchPlaces = async () => {
      const response = await apiRequest<IMapboxSuggestionsResponse>({
        url: debouncedUrl,
      });
      if (response && !isCanceled) {
        const suggestions = response.suggestions;
        // Store in cache, evict oldest if over limit
        if (suggestCache.size >= MAX_SUGGEST_CACHE_SIZE) {
          const firstKey = suggestCache.keys().next().value;
          if (firstKey) suggestCache.delete(firstKey);
        }
        suggestCache.set(debouncedUrl, suggestions);
        setResults(suggestions);
        dispatch(setMapboxPlace(selectBestMapboxSuggestion(debouncedUrl, suggestions)));
      }
    };
    fetchPlaces();
    return () => {
      isCanceled = true;
    };
  }, [debouncedUrl, dispatch, previousUrl]);

  return results;
}

export default useMapboxQuery;
